package ai.wagemix.web.components

import ai.wagemix.web.theme.WMControlCornerVar
import ai.wagemix.web.theme.WMHairlineVar
import ai.wagemix.web.theme.WMMenuMinWidthVar
import ai.wagemix.web.theme.WMBodySmallTextStyle
import ai.wagemix.web.theme.WMTightGapVar
import androidx.compose.runtime.Composable
import ai.wagemix.web.theme.WMInlineGapVar
import ai.wagemix.web.theme.WMIconSmVar
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.tabIndex
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.foundation.layout.Row
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

/** One entry of a [WMMenuButton]. [destructive] items (Delete) are drawn in the error colour. */
interface WMMenuItem {
    val label: String
    val icon: WMIconGlyph?
    val destructive: Boolean
    val onSelect: () -> Unit
}

fun WMMenuItem(label: String, icon: WMIconGlyph? = null, destructive: Boolean = false, onSelect: () -> Unit): WMMenuItem {
    return WMMenuItemData(label, icon, destructive, onSelect)
}

private class WMMenuItemData(
    override val label: String,
    override val icon: WMIconGlyph?,
    override val destructive: Boolean,
    override val onSelect: () -> Unit,
): WMMenuItem

/** The dropdown: a raised surface under the anchor's right edge, above whatever follows it. */
val WMMenuStyle = CssStyle.base {
    val colors = colorMode.toPalette().fs
    Modifier
        .position(Position.Absolute)
        .top(100.percent)
        .right(0.px)
        .zIndex(MENU_Z_INDEX)
        .minWidth(WMMenuMinWidthVar.value())
        .padding(WMTightGapVar.value())
        .gap(WMTightGapVar.value())
        .borderRadius(WMControlCornerVar.value())
        .border(WMHairlineVar.value(), LineStyle.Solid, colors.outlineVariant)
        .backgroundColor(colors.surface)
}

/** The kind of [WMMenuItemStyle], so it can have the destructive variant. */
sealed interface WMMenuItemKind: ComponentKind

/** One row of the dropdown: icon and label, left-aligned, highlighted on hover and keyboard focus. */
val WMMenuItemStyle = CssStyle<WMMenuItemKind> {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .fillMaxWidth()
            .gap(WMInlineGapVar.value())
            .padding(topBottom = WMTightGapVar.value(), leftRight = WMInlineGapVar.value())
            .borderRadius(WMControlCornerVar.value())
            .color(colors.onSurface)
            .cursor(Cursor.Pointer)
            .whiteSpace(WhiteSpace.NoWrap)
    }
    (hover + focusVisible) {
        Modifier.backgroundColor(colorMode.toPalette().fs.surfaceContainer)
    }
}

/** A destructive row (Delete): the error colour. */
val WMDestructiveMenuItemVariant = WMMenuItemStyle.addVariant {
    base { Modifier.color(colorMode.toPalette().fs.error) }
}

/** Covers the page while the menu is open, so a click anywhere else closes it. */
val WMMenuDismissLayerStyle = CssStyle.base {
    Modifier.position(Position.Fixed).top(0.px).left(0.px).right(0.px).bottom(0.px).zIndex(MENU_Z_INDEX - 1)
}

/**
 * An icon button (⋯ by default) that opens [items] in a dropdown floating over the content below it. Choosing an
 * item, or clicking anywhere outside the menu, closes it.
 */
@Composable
fun WMMenuButton(
    accessibilityString: String,
    items: List<WMMenuItem>,
    modifier: Modifier = Modifier,
    glyph: WMIconGlyph = WMIconGlyph.EllipsisVertical,
) {
    var open by remember { mutableStateOf(false) }
    Box(modifier = Modifier.position(Position.Relative).then(modifier)) {
        WMIconButton(glyph, accessibilityString = accessibilityString, size = WMButtonSize.Sm) { open = !open }
        if (open) {
            Box(WMMenuDismissLayerStyle.toModifier().onClick { open = false })
            Column(WMMenuStyle.toModifier().then(WMRaisedStyle.toModifier()).role("menu")) {
                items.forEach { item ->
                    val variant = if (item.destructive) WMDestructiveMenuItemVariant else null
                    Row(
                        modifier = WMMenuItemStyle.toModifier(variant)
                            .role("menuitem")
                            .tabIndex(0)
                            .onClick {
                                open = false
                                item.onSelect()
                            },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        item.icon?.let { WMIcon(it, accessibilityString = null, size = WMIconSmVar.value()) }
                        SpanText(item.label, WMBodySmallTextStyle.toModifier())
                    }
                }
            }
        }
    }
}

/** Above the card grid and its sticky header (10), below dialogs. */
private const val MENU_Z_INDEX = 30
